import { useFormats } from "@portal/hooks/use-formats";
import {
  CustomField,
  NumberBasedField,
} from "@portal/types/entities/custom-field";
import { SelectProps } from "antd/lib";
import dayjs from "dayjs";
import React from "react";
import { match } from "ts-pattern";
import { getCustomFieldOptions, validNumberBasedFields } from "../util";
import DatePickerComp from "./DatePicker";
import NumericEditorCell from "./NumericEditorCell";
import SelectEditorCell from "./SelectEditorCell";
import { TextAreaCellEditor } from "./TextAreaCellEditor";
import TextEditorCell from "./TextEditorCell";
import { CustomFieldTypes } from "./type";

interface CustomFieldFormItemsProps {
  customField: CustomField;
  value: string | number | any;
  onChange: (value?: CustomFieldTypes) => void;
  loading?: boolean;
  handleSubmit: () => void;
  handleSelect: (value: CustomFieldTypes) => void;
  handleClose: () => void;
  handleClear: () => void;
  isFormItem?: boolean;
  defaultValue?: string;
  style?: React.CSSProperties;
  isEditable?: boolean;
}

function CustomFieldFormItems({
  customField,
  value: selectedCustomField,
  onChange: handleSingleValueChange,
  loading = false,
  handleSelect,
  handleClear,
  defaultValue,
  isEditable = true,
}: CustomFieldFormItemsProps) {
  const { locale } = useFormats();
  const precision = customField?.field_type === "DECIMAL" ? 2 : 0;
  const defaultDate =
    selectedCustomField === "-" || !selectedCustomField
      ? undefined
      : dayjs(selectedCustomField);
  function handleCustomFieldOptions() {
    if (customField?.options?.length) {
      return getCustomFieldOptions(
        customField.options,
        customField.field_type,
        locale
      );
    }
    return [];
  }

  const customFieldOptions: SelectProps["options"] = handleCustomFieldOptions();

  // Icons and buttons

  // Form field components
  const SelectCustomField = (
    <SelectEditorCell
      options={customFieldOptions}
      onChange={handleSelect}
      dataType={customField.field_type}
      customOptions={customField.options}
      value={(selectedCustomField as string) ?? defaultValue}
      isLoading={loading}
      style={{ width: "100%" }}
      handleClear={handleClear}
      isEditable={isEditable}
    />
  );

  const TextInput = (
    <>
      {customField.field_type === "PARAGRAPH" ? (
        <TextAreaCellEditor
          onChange={(value) => {
            handleSingleValueChange(value);
          }}
          value={selectedCustomField ?? defaultValue}
          style={{ width: "100%" }}
          handleClear={handleClear}
          maxLength={500}
          isEditable={isEditable}
        />
      ) : (
        <TextEditorCell
          onChange={(value) => {
            if (value) {
              handleSingleValueChange(value);
            }
          }}
          value={selectedCustomField ?? defaultValue}
          style={{ width: "100%", borderColor: "var(--gray-5)" }}
          handleClear={handleClear}
          isEditable={isEditable}
        />
      )}
    </>
  );

  const NumberInput = (
    <NumericEditorCell
      precision={precision}
      onChange={(value) => {
        handleSingleValueChange(value as number);
      }}
      allowNull
      value={selectedCustomField}
      style={{ width: "100%" }}
      handleClear={handleClear}
      isEditable={isEditable}
    />
  );

  const InputCustomField = (
    <>
      {validNumberBasedFields.includes(
        customField.field_type as NumberBasedField[number]
      )
        ? NumberInput
        : TextInput}
    </>
  );

  const DatefieldPicker = (
    <DatePickerComp
      value={defaultDate}
      onChange={(value) => {
        handleSelect(value as string);
      }}
      isEditable={isEditable}
    />
  );

  // Return appropriate field component based on field type
  return match(customField.field_type)
    .with("MULTI_SELECT", () => SelectCustomField)
    .with("SINGLE_SELECT", () => SelectCustomField)
    .with("BOOLEAN", () => SelectCustomField)
    .with("DATETIME", () => DatefieldPicker)
    .with("DATE", () => DatefieldPicker)
    .otherwise(() => InputCustomField);
}

export default CustomFieldFormItems;
